import type { CommandSelectItem, CommandSelectOption } from './types';
import { isCommandSelectGroupTitle, isCommandSelectOption } from './types';

export function normalizeString(str: string): string {
    return str
        ?.toLowerCase()
        ?.normalize('NFD')
        ?.replace(/[\u0300-\u036f]/g, '');
}

export function defaultLocalFilter(query: string, items: CommandSelectItem[]): CommandSelectItem[] {
    if (!query.trim()) {
        return items;
    }

    const normalizedQuery = normalizeString(query);
    const filtered: CommandSelectItem[] = [];

    for (const item of items) {
        if (isCommandSelectGroupTitle(item)) {
            filtered.push(item);
        } else {
            const normalizedLabel = normalizeString(item.label);
            if (normalizedLabel.includes(normalizedQuery)) {
                filtered.push(item);
            }
        }
    }

    return filtered.filter((item, index) => {
        if (isCommandSelectGroupTitle(item)) {
            return filtered.slice(index + 1).some(isCommandSelectOption);
        }
        return true;
    });
}

export function getSelectableOptions(items: CommandSelectItem[]): CommandSelectOption[] {
    return items.filter((item): item is CommandSelectOption => isCommandSelectOption(item) && !item.disabled);
}

export function indexOfSelectableOption(selectableOptions: CommandSelectOption[], option: CommandSelectOption): number {
    return selectableOptions.findIndex((o) => o.id === option.id);
}

export function isFirstSelectableOption(option: CommandSelectOption, items: CommandSelectItem[]): boolean {
    const selectable = getSelectableOptions(items);
    return selectable.length > 0 && selectable[0].id === option.id;
}

export function isEmptyAfterFilter(items: CommandSelectItem[]): boolean {
    return !items.some(isCommandSelectOption);
}

export function debounce<T extends (...args: unknown[]) => unknown>(
    func: T,
    wait: number
): ((...args: Parameters<T>) => void) & { cancel: () => void } {
    let timeout: ReturnType<typeof setTimeout> | null = null;

    const debounced = function executedFunction(...args: Parameters<T>) {
        const later = () => {
            timeout = null;
            func(...args);
        };

        if (timeout) {
            clearTimeout(timeout);
        }
        timeout = setTimeout(later, wait);
    };

    debounced.cancel = () => {
        if (timeout) {
            clearTimeout(timeout);
            timeout = null;
        }
    };

    return debounced;
}
